import React, { useState } from 'react';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import AnimatedSection from '../components/AnimatedSection';
import { ChevronDown, ChevronUp, HelpCircle, Users, Shield, CreditCard, MessageCircle } from 'lucide-react';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        className="w-full py-6 flex justify-between items-center text-left focus:outline-none"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-lg font-medium text-gray-900">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" aria-hidden="true" />
        )}
      </button>
      {isOpen && (
        <div className="pb-6">
          <div className="text-gray-600 prose max-w-none">{answer}</div>
        </div>
      )}
    </div>
  );
}

interface FAQSectionProps {
  title: string;
  icon: React.ReactNode;
  questions: Array<{ question: string; answer: React.ReactNode }>;
  delay?: number;
}

function FAQSection({ title, icon, questions, delay = 0 }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <AnimatedSection delay={delay}>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center gap-3">
          {icon}
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
        <div className="px-6">
          {questions.map((q, index) => (
            <FAQItem
              key={index}
              question={q.question}
              answer={q.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

export default function FAQ() {
  const sections = [
    {
      title: 'General Questions',
      icon: <HelpCircle className="w-6 h-6 text-blue-600" />,
      questions: [
        {
          question: 'What is GearLoop?',
          answer: 'GearLoop is a peer-to-peer rental marketplace for outdoor gear. Whether you need a tent for a weekend camping trip or want to rent out your mountain bike, GearLoop connects outdoor enthusiasts to share high-quality equipment at an affordable price.'
        },
        {
          question: 'How does GearLoop work?',
          answer: (
            <ol className="list-decimal list-inside space-y-2">
              <li>List Your Gear – If you have outdoor equipment sitting unused, create a listing with photos, descriptions, and pricing.</li>
              <li>Rent Gear – Browse available gear in your area and book what you need for your next adventure.</li>
              <li>Pick Up & Explore – Arrange pickup or delivery with the owner, enjoy your trip, and return the item when you're done.</li>
            </ol>
          )
        }
      ]
    },
    {
      title: 'For Renters',
      icon: <Users className="w-6 h-6 text-green-600" />,
      questions: [
        {
          question: 'What types of gear can I rent?',
          answer: 'You can rent a variety of outdoor equipment, including tents, backpacks, kayaks, paddleboards, bikes, climbing gear, and more.'
        },
        {
          question: 'How do I know the gear is in good condition?',
          answer: 'Owners are required to provide clear descriptions and photos of their gear. Renters can also check user reviews and ratings before booking.'
        },
        {
          question: 'What happens if I damage the gear?',
          answer: 'We recommend treating all rented gear with care. If damage occurs, GearLoop offers an optional insurance policy, or renters may be responsible for repair or replacement costs as outlined in the rental agreement.'
        },
        {
          question: 'How do I pick up the gear?',
          answer: 'After booking, you and the owner can arrange a pickup location or delivery method that works best for both of you.'
        }
      ]
    },
    {
      title: 'For Owners (Lenders)',
      icon: <Shield className="w-6 h-6 text-purple-600" />,
      questions: [
        {
          question: 'How do I list my gear?',
          answer: 'Simply create an account, upload photos, set your rental price, and add a description. Once approved, your listing will be live for renters to book.'
        },
        {
          question: 'How much can I earn?',
          answer: 'Earnings depend on the type of gear, condition, and demand in your area. Popular items like paddleboards and mountain bikes can earn £50-£100+ per rental.'
        },
        {
          question: 'Is my gear protected?',
          answer: 'Yes! GearLoop offers optional insurance for owners, ensuring you\'re covered against theft or damage.'
        },
        {
          question: 'Can I decline a rental request?',
          answer: 'Yes, you have full control over who rents your gear. You can accept or decline any booking request.'
        }
      ]
    },
    {
      title: 'Payments & Security',
      icon: <CreditCard className="w-6 h-6 text-orange-600" />,
      questions: [
        {
          question: 'How do payments work?',
          answer: 'Renters pay securely through GearLoop at the time of booking. Owners receive payment once the rental period begins.'
        },
        {
          question: 'Are my details secure?',
          answer: 'Yes, GearLoop uses industry-standard encryption and payment security to protect your information.'
        }
      ]
    },
    {
      title: 'Support',
      icon: <MessageCircle className="w-6 h-6 text-teal-600" />,
      questions: [
        {
          question: 'Is there a membership fee?',
          answer: 'No, signing up is free. GearLoop only takes a small commission from successful rentals to keep the platform running.'
        },
        {
          question: 'How do I contact support?',
          answer: 'If you have any issues, you can reach out to our support team via email or chat.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <SEO 
        title="FAQ - GearLoop | Outdoor Gear Rental Questions Answered"
        description="Find answers to common questions about renting and sharing outdoor gear through GearLoop. Learn about our rental process, insurance, payments, and more."
        keywords={[
          'outdoor gear rental FAQ',
          'gear sharing questions',
          'how to rent outdoor equipment',
          'outdoor gear insurance',
          'rental marketplace safety',
          'peer to peer rental FAQ',
          'outdoor equipment sharing guide',
          'gear rental process'
        ]}
        type="article"
      />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnimatedSection>
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
            <p className="text-lg text-gray-600">
              Find answers to common questions about using GearLoop
            </p>
          </div>
        </AnimatedSection>

        <div className="space-y-6">
          {sections.map((section, index) => (
            <FAQSection
              key={section.title}
              title={section.title}
              icon={section.icon}
              questions={section.questions}
              delay={0.1 * (index + 1)}
            />
          ))}
        </div>

        <AnimatedSection delay={0.6}>
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Still have questions? {' '}
              <a href="/contact" className="text-green-600 hover:text-green-700 font-medium">
                Contact our support team
              </a>
            </p>
          </div>
        </AnimatedSection>
      </main>
      <Footer />
    </div>
  );
}